import { GetServerSidePropsContext } from 'next'

import { BIFROST_API } from '../constants'
import { getTargetHost, getTargetOrigin } from '../utils/bifrost-config'
import { makeRequest, parseJsonResponse } from './api-base'
import { GetCsrfTokenOptions } from './types'

export type VerifySignupTokenResult = {
  response: Response
  data: { email?: string } | undefined
}

function getInternalHeaders({ ctx }: { ctx?: GetServerSidePropsContext }) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (ctx && ctx.req) {
    headers['User-Agent'] = ctx.req.headers['user-agent'] || ''
    headers.Host = getTargetHost(ctx.req.headers.host)
    headers.Origin = getTargetOrigin(ctx.req.headers.host)
    headers.Referer = getTargetOrigin(ctx.req.headers.host)
    headers.Cookie = ctx.req.headers.cookie || ''
  }
  return headers
}

export const internalAuthApi = {
  async getCsrfToken(
    ctx?: GetServerSidePropsContext,
    options?: GetCsrfTokenOptions
  ): Promise<{ csrfToken: string | null }> {
    if (!ctx?.req) return { csrfToken: null }
    const baseUrl = options?.baseUrl || process.env.BIFROST_INTERNAL_URL || ''
    if (!baseUrl) {
      console.error('[CSRF Token] No baseUrl resolved (options.baseUrl or BIFROST_INTERNAL_URL)')
      return { csrfToken: null }
    }
    try {
      const response = await makeRequest(
        `${baseUrl}${BIFROST_API.CSRF_TOKEN}`,
        { ctx },
        getInternalHeaders
      )
      if (!response.ok) return { csrfToken: null }
      if (ctx.res) {
        response.headers.forEach((value, key) => {
          if (key.toLowerCase() === 'set-cookie') {
            ctx.res.appendHeader('Set-Cookie', value)
          }
        })
      }
      const { data } = await parseJsonResponse<{ csrfToken?: string }>(response)
      return { csrfToken: data?.csrfToken || null }
    } catch (err) {
      console.error('[CSRF Token] Failed to get CSRF token from Bifrost:', err)
      return { csrfToken: null }
    }
  },

  async verifySignupToken(
    token: string,
    ctx?: GetServerSidePropsContext
  ): Promise<VerifySignupTokenResult | null> {
    const baseUrl = process.env.BIFROST_INTERNAL_URL || ''
    if (!baseUrl || !token) return null
    try {
      const response = await makeRequest(
        `${baseUrl}${BIFROST_API.SIGNUP.VALIDATE_TOKEN}/${encodeURIComponent(token)}`,
        { method: 'GET', ctx },
        getInternalHeaders
      )
      const parsed = await parseJsonResponse<{ email?: string }>(response)
      return { response, data: parsed.data }
    } catch (err) {
      console.error('[Verify Signup Token] Failed to verify signup token', err)
      return null
    }
  },
}
